import type { ReactNode } from "react";
import {
  ConnectorRail,
  EmptyStatePage,
  GlassCard,
  Glyph,
  makeEntrance,
  usePrefersReducedMotion,
} from "@/components/application/empty-state-page";

const ICON = {
  trigger:
    "M13 2 4.093 12.688c-.348.418-.523.628-.525.804a.5.5 0 0 0 .185.397c.138.111.41.111.955.111H12l-1 8 8.907-10.688c.348-.418.523-.628.525-.804a.5.5 0 0 0-.185-.397c-.138-.111-.41-.111-.955-.111H12l1-8Z",
  wait: "M16.5 17h5l-5 5h5m.45-9c.033-.329.05-.662.05-1 0-5.523-4.477-10-10-10S2 6.477 2 12s4.477 10 10 10a10.11 10.11 0 0 0 1-.05M12 6v6l3.738 1.87",
  send: "M10.5 13.5 21 3M10.627 13.828l2.628 6.758c.232.596.347.893.514.98a.5.5 0 0 0 .462 0c.167-.086.283-.384.515-.979l6.59-16.888c.21-.537.315-.806.258-.977a.5.5 0 0 0-.316-.316c-.172-.057-.44.048-.978.257L3.413 9.253c-.595.233-.893.349-.98.516a.5.5 0 0 0 0 .461c.087.167.385.283.98.514l6.758 2.629c.121.046.182.07.233.106a.5.5 0 0 1 .116.117c.037.05.06.111.107.232Z",
};

type Step = {
  icon: keyof typeof ICON;
  title: string;
  subtitle: string;
  cardClassName?: string;
  titleClassName: string;
  /** The lead step keeps a slow pulse ring on its icon. */
  pulse?: boolean;
};

const STEPS: Step[] = [
  {
    icon: "trigger",
    title: "Trigger",
    subtitle: "Creator joins your roster",
    titleClassName: "text-white",
    pulse: true,
  },
  {
    icon: "wait",
    title: "Wait 2 days",
    subtitle: "Give the welcome kit time to land",
    cardClassName: "opacity-75",
    titleClassName: "text-white/85",
  },
  {
    icon: "send",
    title: "Send message",
    subtitle: "“How’s the first drop going?”",
    cardClassName: "opacity-50",
    titleClassName: "text-white/85",
  },
];

const STEP_DELAYS = [0.35, 0.77, 1.19];
const RAIL_DELAYS = [0.57, 0.99];

type AutomateOutreachEmptyStateProps = {
  onBuild: () => void;
  onStartFromTemplate: () => void;
};

export function AutomateOutreachEmptyState({ onBuild, onStartFromTemplate }: AutomateOutreachEmptyStateProps) {
  const reduceMotion = usePrefersReducedMotion();
  const entrance = makeEntrance(reduceMotion);

  const device: ReactNode = (
    <div className="flex w-full max-w-[540px] flex-col">
      {STEPS.map((step, index) => {
        const delay = STEP_DELAYS[index];
        const isLead = index === 0;
        return (
          <div key={step.title}>
            <GlassCard
              radius="xl"
              className={`backdrop-blur-3xl ${step.cardClassName ?? ""}`}
              style={{ animation: entrance.glassIn(delay) }}
              overlayStyle={{ animation: entrance.fadeIn(delay) }}
            >
              <div className="flex items-start gap-4 px-5 py-4" style={{ animation: entrance.fadeInUp(delay) }}>
                <span
                  aria-hidden="true"
                  className={
                    isLead
                      ? "inline-flex size-9 shrink-0 items-center justify-center rounded-lg bg-brand-200 text-unbox-dark-brand-start"
                      : "inline-flex size-9 shrink-0 items-center justify-center rounded-lg border border-white/15 bg-white/12 text-brand-100 backdrop-blur-sm"
                  }
                  style={
                    step.pulse && !reduceMotion
                      ? { animation: "trigger-pulse 2.6s ease-out 1.89s infinite" }
                      : undefined
                  }
                >
                  <Glyph d={ICON[step.icon]} />
                </span>
                <div className="flex flex-col gap-0.5 pt-1">
                  <p className={`text-base font-semibold ${step.titleClassName}`}>{step.title}</p>
                  <p className="text-sm text-brand-100">{step.subtitle}</p>
                </div>
              </div>
            </GlassCard>
            {index < RAIL_DELAYS.length && (
              <ConnectorRail delaySeconds={RAIL_DELAYS[index]} reduceMotion={reduceMotion} />
            )}
          </div>
        );
      })}
    </div>
  );

  return (
    <EmptyStatePage
      eyebrow="Automate Outreach"
      headline="Set it once, run it forever"
      subline="Pick a trigger, stack the steps, and every creator gets the right message at the right time, whether that’s ten creators or a thousand. Nothing to remember, nobody to chase."
      measureCh={42}
      portraitSrc="/empty-states/portrait-automate-outreach.webp"
      portraitMaskLeft="38%"
      actionsDelaySeconds={1.49}
      device={device}
      primaryAction={{ label: "Build an automation", onClick: onBuild }}
      secondaryLink={{ label: "Or start from a template", onClick: onStartFromTemplate }}
    />
  );
}
